// Map settings panel for map editor

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Map settings panel component
 * Allows editing global map properties in the map editor
 */
class MapSettingsPanel extends BaseComponent {
  static properties = {
    globalIllumination: { type: String, attribute: 'global-illumination' },
  };

  static styles = css`
    :host {
      display: block;
    }

    .map-settings-panel {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.9);
      border: 1px solid #555;
      border-radius: 8px;
      padding: 16px;
      z-index: 1000;
      min-width: 280px;
      color: #fff;
    }

    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    }

    .panel-header h3 {
      margin: 0;
      font-size: 16px;
      font-weight: bold;
    }

    .dismiss-button {
      background: none;
      border: none;
      color: #fff;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s;
    }

    .dismiss-button:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }

    .setting-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    .setting-row:last-child {
      margin-bottom: 0;
    }

    .setting-row label {
      font-size: 14px;
      margin-right: 12px;
    }

    .setting-row select {
      padding: 6px 12px;
      background: #333;
      border: 1px solid #555;
      border-radius: 4px;
      color: #fff;
      font-size: 14px;
      cursor: pointer;
      min-width: 120px;
    }

    .setting-row select:focus {
      outline: none;
      border-color: #0d6efd;
    }

    .save-button {
      width: 100%;
      padding: 10px;
      background: #0d6efd;
      border: none;
      border-radius: 6px;
      color: #fff;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }

    .save-button:hover {
      background: #0b5ed7;
    }
  `;

  constructor() {
    super();
    this.globalIllumination = 'none';
    this._selectedIllumination = 'none';
  }

  /** @param {Map<string, unknown>} changedProps */
  willUpdate(changedProps) {
    if (changedProps.has('globalIllumination')) {
      this._selectedIllumination = this.globalIllumination;
    }
  }

  /** @param {Event} e */
  _handleIlluminationChange(e) {
    this._selectedIllumination = /** @type {HTMLSelectElement} */ (e.target).value;
  }

  _handleSave() {
    this.dispatchEvent(
      new CustomEvent('save', {
        detail: {
          globalIllumination: this._selectedIllumination,
        },
        bubbles: true,
      }),
    );
  }

  _handleDismiss() {
    this.dispatchEvent(new CustomEvent('dismiss', { bubbles: true }));
  }

  render() {
    return html`
      <div class="map-settings-panel">
        <div class="panel-header">
          <h3>Map Settings</h3>
          <button class="dismiss-button" title="Close" @click="${this._handleDismiss}">×</button>
        </div>
        <div class="setting-row">
          <label for="global-illumination">Global Illumination:</label>
          <select
            id="global-illumination"
            .value="${this._selectedIllumination}"
            @change="${this._handleIlluminationChange}"
          >
            <option value="none">None (Dark)</option>
            <option value="dim">Dim Light</option>
            <option value="bright">Bright Light</option>
          </select>
        </div>
        <button class="save-button" @click="${this._handleSave}">Save Settings</button>
      </div>
    `;
  }
}

customElements.define('map-settings-panel', MapSettingsPanel);
